import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { BlogApi } from "../../service";
import APPLICATION_SETTINGS from "../../settings";
import useApiHandler from "../hooks/ApiHandler";

const BlogListPage = () => {
  const { callApi, loading } = useApiHandler();
  const [categories, setCategories] = useState([]);
  const [activeCategoryId, setActiveCategoryId] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState(null);

  const normalizedSearch = searchTerm.trim().toLowerCase();

  const fetchCategories = async () => {
    setError(null);
    try {
      await callApi(() => BlogApi.getCategories(), {
        onSuccess: (response) => {
          const data = response?.data || [];
          setCategories(data);
        },
      });
    } catch (err) {
      setError("Unable to load blog categories. Please try again later.");
    }
  };

  useEffect(() => {
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const categoriesWithSearchApplied = useMemo(() => {
    if (!normalizedSearch) {
      return categories.map((category) => ({
        ...category,
        filteredPosts: category.posts || [],
      }));
    }

    return categories
      .map((category) => {
        const matchingPosts = (category.posts || []).filter((post) =>
          (post.title || "").toLowerCase().includes(normalizedSearch)
        );

        return {
          ...category,
          filteredPosts: matchingPosts,
        };
      })
      .filter((category) => category.filteredPosts.length > 0);
  }, [categories, normalizedSearch]);

  const categoriesMatchingSearch = useMemo(() => {
    if (!normalizedSearch) return categoriesWithSearchApplied;
    return categoriesWithSearchApplied;
  }, [categoriesWithSearchApplied, normalizedSearch]);

  const visibleCategories = useMemo(() => {
    if (normalizedSearch || activeCategoryId === "all") {
      return categoriesMatchingSearch;
    }
    const selectedCategory = categoriesMatchingSearch.find(
      (category) => category.id === activeCategoryId
    );
    return selectedCategory ? [selectedCategory] : [];
  }, [categoriesMatchingSearch, activeCategoryId, normalizedSearch]);

  const hasVisiblePosts = useMemo(
    () =>
      visibleCategories.some((category) =>
        (normalizedSearch ? category.filteredPosts : category.posts)?.length
      ),
    [visibleCategories]
  );

  const showNoResults =
    !loading &&
    ((normalizedSearch && visibleCategories.length === 0) ||
      (!normalizedSearch && !hasVisiblePosts));

  const formatDate = (value) => {
    if (!value) return "";
    return new Date(value).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const buildImageUrl = (path) => {
    if (!path) return null;
    try {
      return new URL(path, APPLICATION_SETTINGS.VITE_API_BASE).toString();
    } catch (error) {
      return path;
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-12 pt-[130px]">
      <div className="text-center mb-10">
        <p className="text-[#3CAB5C] text-sm font-semibold uppercase tracking-[0.2em] mb-3">
          Blog
        </p>
        <h1 className="text-3xl md:text-4xl font-semibold text-[#242424] mb-4">
          Insights & Updates from Properteasy
        </h1>
        {/*<p className="text-[#595959] text-base md:text-lg max-w-3xl mx-auto">*/}
        {/*  Explore property maintenance tips, homeowner guides, vendor stories, and product updates straight from the Properteasy team.*/}
        {/*</p>*/}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between mb-8">
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            className={`px-4 py-2 rounded-full border text-sm font-semibold transition-colors ${
              activeCategoryId === "all"
                ? "bg-[#3CAB5C] border-[#3CAB5C] text-white"
                : "border-[#ECECEC] text-[#242424]"
            }`}
            onClick={() => setActiveCategoryId("all")}
          >
            All Topics
          </button>
          {categories.map((category) => (
            <button
              key={category.id}
              type="button"
              className={`px-4 py-2 rounded-full border text-sm font-semibold transition-colors ${
                activeCategoryId === category.id
                  ? "bg-[#3CAB5C] border-[#3CAB5C] text-white"
                  : "border-[#ECECEC] text-[#242424]"
              }`}
              onClick={() => setActiveCategoryId(category.id)}
            >
              {category.name}
            </button>
          ))}
        </div>

        <div className="w-full md:w-64">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by category name..."
            className="w-full border border-[#ECECEC] rounded-full px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#3CAB5C]"
          />
        </div>
      </div>

      {loading && (
        <div className="text-center text-[#595959] mb-6">Loading posts...</div>
      )}

      {visibleCategories.map((category) => (
        <section key={category.id} className="mb-10">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-2xl font-semibold text-[#242424]">
                {category.name}
              </h2>
              {category.description ? (
                <p className="text-sm text-[#595959]">{category.description}</p>
              ) : null}
            </div>
          </div>

          {(() => {
            const postsToRender = normalizedSearch
              ? category.filteredPosts
              : category.posts;
            return postsToRender && postsToRender.length ? (
              <div className="grid gap-6 md:grid-cols-2">
                {postsToRender.map((post) => (
                  <article
                    key={post.id}
                    className="border border-[#ECECEC] rounded-2xl overflow-hidden bg-white hover:shadow-lg transition-shadow"
                  >
                    {post.heroImage && (
                      <div className="h-52 w-full overflow-hidden">
                        <img
                          src={buildImageUrl(post.heroImage)}
                          alt={post.title}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </div>
                    )}
                    <div className="p-6 flex flex-col gap-4">
                      <div>
                        <p className="text-xs text-[#3CAB5C] font-semibold uppercase tracking-[0.3em] mb-2">
                          {formatDate(post.publishedAt)}
                        </p>
                        <h3 className="text-xl font-semibold text-[#242424] mb-2">
                          {post.title}
                        </h3>
                        <p className="text-sm text-[#595959] leading-relaxed">
                          {post.excerpt}
                        </p>
                      </div>
                      <div className="flex items-center justify-between text-sm text-[#595959]">
                        <Link
                          to={`/blog/${post.slug}`}
                          className="text-[#3CAB5C] font-semibold hover:underline"
                        >
                          Read more
                        </Link>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div className="border border-dashed border-[#ECECEC] rounded-2xl p-6 text-center text-[#595959]">
                No posts in this category yet.
              </div>
            );
          })()}
        </section>
      ))}

      {showNoResults && (
        <div className="text-center text-[#595959] mt-8">
          {normalizedSearch
            ? "No posts match your search."
            : "No posts are available for this category yet."}
        </div>
      )}
    </div>
  );
};

export default BlogListPage;
